import React from "react";
import { Dialog, Icon, Input } from "../components/DesignSystem";
import type { RouteKey } from "../domain/types";
import { usePlannerData } from "../state/PlannerDataContext";

const TYPE_ICON: Record<string, string> = {
  guest: "user",
  household: "home",
  vendor: "briefcase",
  venue: "map-pin",
  document: "file-text",
  task: "check-square"
};

/** Global search (Wave 4 §18) -- opened as a contextual action from the topbar search box, never a
 * permanent nav item of its own. Debounced live search across guests/households/vendors/venues/
 * documents/tasks; picking a result navigates to that screen. */
export function GlobalSearchDialog({ onClose, onNavigate }: { onClose: () => void; onNavigate: (route: RouteKey) => void }) {
  const { service } = usePlannerData();
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<Array<{ type: string; id: string; label: string; meta?: string; route: string }>>([]);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      service.search
        .query(query)
        .then(setResults)
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(handle);
  }, [query, service]);

  return (
    <Dialog title="Search" onClose={onClose} width={520}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search guests, households, vendors, venues, documents, tasks"
          icon="search"
          style={{ width: "100%" }}
        />
        {query.trim().length > 0 && query.trim().length < 2 && (
          <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>Keep typing...</span>
        )}
        {loading && <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>Searching...</span>}
        {!loading && query.trim().length >= 2 && results.length === 0 && (
          <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>No matches.</span>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
          {results.map((r) => (
            <button
              key={`${r.type}-${r.id}`}
              type="button"
              onClick={() => { onNavigate(r.route as RouteKey); onClose(); }}
              style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-2) var(--space-3)", border: 0, background: "transparent", borderRadius: "var(--radius-md)", cursor: "pointer", textAlign: "left" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ivory-200)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <Icon name={TYPE_ICON[r.type] || "circle"} size={15} style={{ color: "var(--text-tertiary)" }} />
              <span style={{ flex: 1, font: "var(--type-body)" }}>{r.label}</span>
              {r.meta && <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{r.meta}</span>}
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
